<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" @click.self="$emit('close')">
    <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">

      <!-- Header -->
      <div class="flex items-start justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100 truncate">{{ local.title }}</h3>
            <span :class="badgeClass" class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap">
              <span v-if="local.status === 'running'" class="w-2.5 h-2.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
              {{ statusLabel }}
            </span>
          </div>
          <p class="text-xs text-slate-400 mt-0.5 truncate">
            {{ local.inbox_name }} · template <span class="font-mono">{{ local.template_name }}</span>
          </p>
        </div>
        <button class="text-slate-400 hover:text-slate-600 text-xl leading-none flex-shrink-0" @click="$emit('close')">✕</button>
      </div>

      <div class="overflow-y-auto flex-1 px-6 py-5 space-y-5">

        <!-- Stats cards -->
        <div class="grid grid-cols-3 sm:grid-cols-6 gap-3">
          <div
            v-for="card in cards"
            :key="card.key"
            class="rounded-xl border p-3 text-center transition-colors"
            :class="card.filter
              ? (leadFilter === card.filter
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 cursor-pointer'
                  : 'border-slate-100 dark:border-slate-800 cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/40 dark:hover:bg-emerald-900/10')
              : 'border-slate-100 dark:border-slate-800'"
            @click="card.filter && toggleCardFilter(card.filter)"
          >
            <div class="text-2xl font-bold" :class="card.color">{{ stats[card.key] || 0 }}</div>
            <div class="text-xs text-slate-400 mt-0.5">{{ card.label }}</div>
          </div>
        </div>

        <!-- Progress bar -->
        <div>
          <div class="flex items-center justify-between mb-1.5 text-xs text-slate-400">
            <span>Progresso</span>
            <span class="font-semibold text-slate-500 dark:text-slate-400">{{ stats.progress || 0 }}%</span>
          </div>
          <div class="bg-slate-100 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
            <div class="h-2 rounded-full bg-emerald-500 transition-all duration-700" :style="{ width: (stats.progress || 0) + '%' }"></div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex flex-wrap gap-2">
          <button
            v-if="canTrigger"
            class="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            :disabled="acting"
            @click="doAction('trigger')"
          >▶️ Disparar</button>
          <button
            v-if="local.status === 'running'"
            class="px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50"
            :disabled="acting"
            @click="doAction('pause')"
          >⏸ Pausar</button>
          <button
            v-if="local.status === 'paused'"
            class="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            :disabled="acting"
            @click="doAction('resume')"
          >▶️ Retomar</button>
          <button
            v-if="['running','paused','scheduled'].includes(local.status)"
            class="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
            :disabled="acting"
            @click="showCancelModal = true"
          >🚫 Cancelar</button>
          <button
            class="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            :disabled="exporting || !local.id"
            :title="exporting ? 'Gerando planilha...' : 'Baixar XLSX com todos os leads'"
            @click="exportXlsx"
          >
            <span :class="exporting ? 'i-lucide-loader animate-spin' : 'i-lucide-download'" class="w-4 h-4"></span>
            {{ exporting ? 'Gerando…' : 'Exportar Excel' }}
          </button>
          <div class="flex-1"></div>
          <span v-if="connected" class="inline-flex items-center gap-1.5 text-xs text-emerald-500 self-center">
            <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> tempo real
          </span>
        </div>

        <!-- Leads table -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-200">Leads</h4>
            <select v-model="leadFilter" class="text-xs border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300" @change="reloadLeads">
              <option :value="null">Todos</option>
              <option value="queued">Na fila</option>
              <option value="sent">Enviados</option>
              <option value="delivered">Entregues</option>
              <option value="read">Lidos</option>
              <option value="replied">Respondidos</option>
              <option value="failed">Falhas</option>
            </select>
          </div>
          <div class="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-slate-50 dark:bg-slate-800/60">
                <tr class="text-left text-xs text-slate-400 uppercase">
                  <th class="px-3 py-2 font-semibold">Telefone</th>
                  <th class="px-3 py-2 font-semibold">Nome</th>
                  <th class="px-3 py-2 font-semibold">Status</th>
                  <th class="px-3 py-2 font-semibold">Conversa</th>
                  <th class="px-3 py-2 font-semibold">Detalhe</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                <tr v-for="l in leads" :key="l.id" class="text-slate-700 dark:text-slate-200">
                  <td class="px-3 py-2 font-mono text-xs">{{ l.phone_number }}</td>
                  <td class="px-3 py-2 truncate max-w-[10rem]">{{ l.name || '—' }}</td>
                  <td class="px-3 py-2">
                    <span :class="leadBadge(l.status)" class="px-2 py-0.5 rounded-full text-xs font-medium">{{ leadLabel(l.status) }}</span>
                  </td>
                  <td class="px-3 py-2 text-xs">
                    <a
                      v-if="l.conversation_display_id"
                      :href="`/app/accounts/${accountId}/conversations/${l.conversation_display_id}`"
                      target="_blank"
                      rel="noopener"
                      class="text-emerald-600 hover:text-emerald-700 underline cursor-pointer inline-flex items-center gap-1"
                    >Abrir <span class="i-lucide-external-link w-3.5 h-3.5"></span></a>
                    <span v-else class="text-slate-300">—</span>
                  </td>
                  <td class="px-3 py-2 text-xs text-red-500">
                    <span v-if="l.status === 'failed'">{{ l.meta_error_message || ('erro ' + (l.meta_error_code || '')) }}</span>
                    <span v-else class="text-slate-300">—</span>
                  </td>
                </tr>
                <tr v-if="!leads.length">
                  <td colspan="5" class="px-3 py-6 text-center text-slate-400 text-sm">Nenhum lead</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="leadTotal > leadPer" class="flex items-center justify-between mt-2 text-xs text-slate-400">
            <button class="px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40" :disabled="leadPage <= 1" @click="changePage(-1)">‹ Anterior</button>
            <span>Página {{ leadPage }} de {{ Math.ceil(leadTotal / leadPer) }}</span>
            <button class="px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40" :disabled="leadPage >= Math.ceil(leadTotal / leadPer)" @click="changePage(1)">Próxima ›</button>
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- Sprint #47.23: modal de confirmacao de cancelamento (TERMINAL) -->
  <div v-if="showCancelModal" class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" @click.self="showCancelModal = false">
    <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6">
      <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">⚠️ Cancelar campanha?</h3>
      <p class="text-sm text-slate-600 dark:text-slate-300 mb-1">
        Esta ação é <strong class="text-red-600">irreversível</strong>. A campanha será marcada como cancelada.
      </p>
      <p class="text-sm text-slate-600 dark:text-slate-300 mb-5">
        <strong>{{ queuedCount }} lead{{ queuedCount === 1 ? '' : 's' }} ainda na fila</strong> não ser{{ queuedCount === 1 ? 'á' : 'ão' }} enviado{{ queuedCount === 1 ? '' : 's' }}.
      </p>
      <div class="flex gap-2 justify-end">
        <button class="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" @click="showCancelModal = false">Voltar</button>
        <button class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50" :disabled="acting" @click="doCancel">{{ acting ? 'Cancelando...' : 'Sim, cancelar' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { emitter } from 'shared/helpers/mitt';
import CampaignsApi from '../../../../../api/bmtWhatsappOfficialCampaigns';

const LEAD_PER = 50;

export default {
  name: 'BmtWhatsappOfficialCampaignDetail',
  props: {
    campaign: { type: Object, required: true },
  },
  emits: ['close', 'refresh'],
  setup(props, { emit }) {
    const route = useRoute();
    const accountId = route.params.accountId;
    const local = reactive({ ...props.campaign });
    const stats = ref({ ...(props.campaign.stats || {}) });
    const leads = ref([]);
    const leadFilter = ref(null);
    const leadPage = ref(1);
    const leadTotal = ref(0);
    const leadPer = LEAD_PER;
    const acting = ref(false);
    const showCancelModal = ref(false);
    const exporting = ref(false);
    const connected = ref(false);
    let pollTimer = null;

    const cards = [
      { key: 'sent', label: 'Enviadas', color: 'text-blue-600', filter: 'sent' },
      { key: 'delivered', label: 'Entregues', color: 'text-indigo-600', filter: 'delivered' },
      { key: 'read', label: 'Lidas', color: 'text-woot-600', filter: 'read' },
      { key: 'replied', label: 'Respondidas', color: 'text-emerald-600', filter: 'replied' },
      { key: 'failed', label: 'Falhas', color: 'text-red-500', filter: 'failed' },
      { key: 'total', label: 'Total', color: 'text-slate-600 dark:text-slate-300', filter: null },
    ];

    function toggleCardFilter(status) {
      leadFilter.value = leadFilter.value === status ? null : status;
      leadPage.value = 1;
      reloadLeads();
    }

    const statusLabel = computed(() => ({
      draft: 'Rascunho', scheduled: 'Agendada', running: 'Disparando',
      paused: 'Pausada', completed: 'Concluída', cancelled: 'Cancelada', failed: 'Falhou',
    }[local.status] || local.status));

    const badgeClass = computed(() => ({
      running: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      paused: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      completed: 'bg-woot-100 text-woot-700 dark:bg-woot-900/30 dark:text-woot-400',
      failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    }[local.status] || 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'));

    const canTrigger = computed(() => ['draft', 'scheduled'].includes(local.status) && (stats.value.queued || 0) > 0);
    const queuedCount = computed(() => (stats.value.queued || 0) + (stats.value.sending || 0));

    function leadLabel(s) {
      return { queued: 'Na fila', sending: 'Enviando', sent: 'Enviado', delivered: 'Entregue', read: 'Lido', replied: 'Respondeu', failed: 'Falha', opted_out: 'Opt-out' }[s] || s;
    }
    function leadBadge(s) {
      if (s === 'failed') return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
      if (s === 'read' || s === 'replied') return 'bg-woot-100 text-woot-600 dark:bg-woot-900/30 dark:text-woot-400';
      if (s === 'delivered') return 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400';
      if (s === 'sent') return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
      return 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300';
    }

    async function reloadStats() {
      try {
        const { data } = await CampaignsApi.stats(local.id);
        stats.value = data;
      } catch { /* ignore */ }
    }

    async function reloadLeads() {
      try {
        const { data } = await CampaignsApi.leads(local.id, { page: leadPage.value, status: leadFilter.value });
        leads.value = data.leads || [];
        leadTotal.value = data.total || 0;
      } catch { leads.value = []; }
    }

    function changePage(dir) {
      leadPage.value = Math.max(1, leadPage.value + dir);
      reloadLeads();
    }

    async function exportXlsx() {
      if (!local.id || exporting.value) return;
      exporting.value = true;
      try {
        const res = await CampaignsApi.exportLeads(local.id);
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const cd = res.headers?.['content-disposition'] || '';
        const match = cd.match(/filename="?([^";]+)"?/);
        const filename = match ? match[1] : `campanha_${local.title || 'export'}.xlsx`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        alert(err?.response?.data?.error || 'Erro ao gerar planilha');
      } finally {
        exporting.value = false;
      }
    }

    async function doAction(action) {
      acting.value = true;
      try {
        await CampaignsApi[action](local.id);
        await reloadStats();
        await reloadLeads();
        // status local otimista
        if (action === 'trigger' || action === 'resume') local.status = 'running';
        if (action === 'pause') local.status = 'paused';
        emit('refresh');
      } catch (err) {
        alert(err?.response?.data?.error || 'Erro na ação');
      } finally {
        acting.value = false;
      }
    }

    async function doCancel() {
      acting.value = true;
      try {
        const { data } = await CampaignsApi.cancel(local.id);
        local.status = 'cancelled';
        showCancelModal.value = false;
        if (data?.campaign?.stats) stats.value = { ...stats.value, ...data.campaign.stats };
        await reloadStats();
        await reloadLeads();
        emit('refresh');
      } catch (err) {
        alert(err?.response?.data?.error || 'Erro ao cancelar');
      } finally {
        acting.value = false;
      }
    }

    function onWsUpdate(data) {
      if (!data || data.id !== local.id) return;
      connected.value = true;
      if (data.counts) {
        stats.value = { ...stats.value, ...data.counts, progress: computeProgress(data.counts) };
      }
      if (data.status) local.status = data.status;
    }

    function computeProgress(counts) {
      const total = counts.total || 0;
      if (!total) return 0;
      // Sprint #47.24: alinhado com backend stats_json (processados, nao sucesso)
      const done = total - (counts.queued || 0) - (counts.sending || 0);
      return Math.round((done / total) * 100);
    }

    onMounted(() => {
      reloadStats();
      reloadLeads();
      emitter.on('BMT_WHATSAPP_CAMPAIGN_UPDATED', onWsUpdate);
      // polling fallback
      pollTimer = setInterval(() => {
        if (['running', 'scheduled'].includes(local.status)) {
          reloadStats();
          reloadLeads();
        }
      }, 12000);
    });

    onUnmounted(() => {
      emitter.off('BMT_WHATSAPP_CAMPAIGN_UPDATED', onWsUpdate);
      if (pollTimer) clearInterval(pollTimer);
    });

    return {
      local, stats, leads, leadFilter, leadPage, leadTotal, leadPer,
      acting, exporting, connected, cards, statusLabel, badgeClass, canTrigger,
      showCancelModal, queuedCount, doCancel,
      exportXlsx,
      leadLabel, leadBadge, reloadLeads, changePage, doAction,
      accountId, toggleCardFilter,
    };
  },
};
</script>
